import { useEffect, useState, useRef } from 'react';
import { X, ShoppingBag } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFeatureSettings } from '@/hooks/useFeatureSettings';
import { cn } from '@/lib/utils';

interface RecentOrder {
  productName: string;
  productImage: string | null;
  city: string;
  minutesAgo: number;
}

const LITHUANIAN_CITIES = [
  'Vilnius', 'Kaunas', 'Klaipėda', 'Šiauliai', 'Panevėžys',
  'Alytus', 'Marijampolė', 'Utena', 'Telšiai', 'Tauragė'
];

const SocialProofPopup = () => {
  const { t } = useLanguage();
  const { data: features } = useFeatureSettings();
  const [visible, setVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<RecentOrder | null>(null);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const orderIndexRef = useRef(0);
  const [dismissed, setDismissed] = useState(false);

  const isEnabled = features?.social_proof_enabled ?? true;
  const minInterval = (features?.social_proof_min_interval_seconds ?? 55) * 1000;
  const maxInterval = (features?.social_proof_max_interval_seconds ?? 95) * 1000;
  const displayDuration = (features?.social_proof_display_seconds ?? 5) * 1000;
  const initialDelay = (features?.social_proof_initial_delay_seconds ?? 8) * 1000;

  // Fetch recent order items on mount
  useEffect(() => {
    if (!isEnabled) return;

    const fetchRecentOrders = async () => {
      const { data } = await supabase
        .from('order_items')
        .select('product_name, product_image, created_at')
        .order('created_at', { ascending: false })
        .limit(20);

      if (data && data.length > 0) {
        const orders: RecentOrder[] = data.map((item) => {
          const minutesAgo = Math.floor(
            (Date.now() - new Date(item.created_at || '').getTime()) / 60000
          );
          return {
            productName: item.product_name,
            productImage: item.product_image,
            city: LITHUANIAN_CITIES[Math.floor(Math.random() * LITHUANIAN_CITIES.length)],
            minutesAgo: Math.min(minutesAgo, 1440),
          };
        });
        setRecentOrders(orders);
      }
    };

    fetchRecentOrders();
  }, [isEnabled]);



  // Show popup at configured intervals using recursive setTimeout for truly random intervals
  useEffect(() => {
    if (recentOrders.length === 0 || dismissed || !isEnabled) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    let isFirst = true;

    const scheduleNext = () => {
      const delay = isFirst
        ? initialDelay
        : minInterval + Math.random() * (maxInterval - minInterval);
      isFirst = false;

      timeoutId = setTimeout(() => {
        if (!dismissed) {
          const idx = orderIndexRef.current % recentOrders.length;
          setCurrentOrder(recentOrders[idx]);
          setVisible(true);
          orderIndexRef.current += 1;
          setTimeout(() => setVisible(false), displayDuration);
        }
        scheduleNext();
      }, delay);
    };

    scheduleNext();

    return () => clearTimeout(timeoutId);
  }, [recentOrders, dismissed, isEnabled, minInterval, maxInterval, initialDelay, displayDuration]);

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return t.socialProof?.minutesAgo?.replace('{n}', String(Math.max(1, minutes))) 
        || `prieš ${Math.max(1, minutes)} min.`;
    }
    const hours = Math.floor(minutes / 60);
    return t.socialProof?.hoursAgo?.replace('{n}', String(hours)) 
      || `prieš ${hours} val.`;
  };

  if (!currentOrder || dismissed || !isEnabled) return null;

  return (
    <div
      className={cn(
        "fixed bottom-24 left-4 z-50 max-w-xs w-full",
        "bg-card border border-border rounded-xl shadow-strong",
        "transform transition-all duration-500 ease-out",
        "lg:bottom-6",
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-4 opacity-0 pointer-events-none"
      )}
    >
      <div className="flex items-start gap-3 p-3.5">
        <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-muted">
          {currentOrder.productImage ? (
            <img
              src={currentOrder.productImage}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingBag className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">
            {t.socialProof?.someoneFrom || 'Kažkas iš'}{' '}
            <span className="font-semibold text-foreground">{currentOrder.city}</span>{' '}
            {t.socialProof?.justBought || 'ką tik įsigijo'}
          </p>
          <p className="text-sm font-medium text-foreground mt-0.5 line-clamp-2 leading-tight">
            {currentOrder.productName}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            {formatTime(currentOrder.minutesAgo)}
          </p>
        </div>

        <button
          onClick={() => {
            setVisible(false);
            setDismissed(true);
          }}
          className="flex-shrink-0 p-1 rounded-full hover:bg-muted transition-colors"
          aria-label={t.socialProof?.close || 'Uždaryti'}
        >
          <X className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>

      <div className="border-t border-border px-3.5 py-1.5 flex items-center gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
        <span className="text-[10px] text-muted-foreground">
          {t.socialProof?.verifiedPurchase || 'Patvirtintas pirkimas'}
        </span>
      </div>
    </div>
  );
};

export default SocialProofPopup;
